import { FC, useState } from 'react';

import { css } from '@emotion/react';
import { ToucanComponents } from '@jointoucan/toucan-design';
import { useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { FieldErrors } from 'react-hook-form';

import PasswordMinCharactersDisplay from '~/components/shared/PasswordMinCharactersDisplay';
import { SettingTypes } from '~/constants/dashboardTypes';

const { TextField, Checkbox } = ToucanComponents;

type FormValues = {
  name: string;
  newPassword: string;
};

interface EditFieldsProps {
  register: any; // NOTE: using type `any` as the types provided by react-hook-form were not resolving https://github.com/react-hook-form/react-hook-form/issues/580
  errors: FieldErrors<FormValues>;
  hasErrorWithCurrentPassword: boolean;
  editType: string;
  passwordLength: number;
}

const EditFields: FC<EditFieldsProps> = ({
  register,
  errors,
  hasErrorWithCurrentPassword,
  editType,
  passwordLength,
}) => {
  const [isPasswordRevealed, setIsPasswordRevealed] = useState(true);
  const theme = useTheme();
  const { t } = useTranslation('dashboard');

  const editFields = (editFieldType: string) => {
    switch (editFieldType) {
      case SettingTypes.name:
        return (
          <TextField
            label={t('name')}
            name="name"
            hasError={!!errors.name}
            helperText={errors.name && errors.name.message}
            {...register('name', {
              required: `${t('pleaseIncludeYourName')}`,
            })}
          />
        );
      case SettingTypes.password:
        return (
          <>
            <TextField
              label={t('currentPassword')}
              hasError={hasErrorWithCurrentPassword}
              helperText={hasErrorWithCurrentPassword ? t('passwordDoesntMatch') : ''}
              {...register('currentPassword')}
              type={isPasswordRevealed ? 'text' : 'password'}
              hideCta={t('hide')}
              showCta={t('show')}
            />
            <TextField
              label={t('newPassword')}
              hasError={!!errors.newPassword}
              helperText={
                errors.newPassword ? (
                  errors.newPassword.message
                ) : (
                  <PasswordMinCharactersDisplay passwordLength={passwordLength} />
                )
              }
              {...register('newPassword', {
                required: t('passwordAtleastNumCharacters', { numCharacters: 8 }),
                minLength: {
                  value: 8,
                  message: t('passwordAtleastNumCharacters', { numCharacters: 8 }),
                },
              })}
              type={isPasswordRevealed ? 'text' : 'password'}
              css={css`
                margin-top: ${theme.spacing(1)};
              `}
              hideCta={t('hide')}
              showCta={t('show')}
            />
            <div
              css={css`
                margin-top: ${theme.spacing(2)};
              `}
            >
              <Checkbox
                isChecked={isPasswordRevealed}
                label={t('showPassword')}
                value="show/hide password"
                onChange={() => setIsPasswordRevealed((prevState) => !prevState)}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return <>{editFields(editType)}</>;
};

export default EditFields;
